import { SketchLineObject, SketchArcObject, SketchEllipseObject, SketchSplineObject, SketchPointObject, SketchPointKind, SketchParabolaObject, SketchEllipseControlPointFactory } from '../../mesh';
import { PointsMaterialFactory, LineMaterialFactory, SketchEdgeBaseStyle, LineMaterialState } from '../../material';
import { Vector3 } from 'three';
import type { ReferencePlane } from '../../reference';
import type { CutPairResult } from './type';

/**
 * 等距实体截取处理 —— SketchExtensionAndIntersectionProcessing 的截取部分拆分。
 * 唯一功能: 已知两实体延长后的交点, 对每一实体截取多余的线段, 得到完整连续轮廓;
 *  交点顶替原始链相连端成为新端点, 样条曲线额外区分交点位于曲线内部/延长线上
 *  两种情形（曲线内部时删除交点与较近端点之间的曲线段）。
 */
export class SketchEntityCut {

    //// 根据输入的交点, 截取多余的线段, 得到完整轮廓 输入SketchXXXObject 输出输入SketchXXXObject

    /**
     * 已知两条线段延长之后的交点
     * 对于line1, 用交点顶替"原始链中与相邻实体相连"的端点成为新的一端(原来A是startPoint或endPoint, 交点就是什么, 这个类型不变)
     * 对于line2, 和line1同理
     * 返回新的line1和line2
     * 
     * 实现说明：
     * 1. 对每条线段分别判定截取侧：以入参 side（原始链解析的相连端）为准，交点替换相连端
     *    （保留其角色：被替换的是 startPoint 则交点成为新 startPoint，是 endPoint 则交点
     *    成为新 endPoint）。偏移距离增大时交点可能越过另一端点，旧"离交点最近"启发式
     *    会选错截取侧，故截取侧必须由偏移前拓扑决定；
     * 2. 另一端的原位置克隆为独立控制点，交点位置克隆为独立控制点——
     *    两条新线不共享任何控制点实例，避免对象树/父级归属冲突；
     * 3. 不修改原始 line1/line2，返回两条独立的新线段。
     *    边界线段（开放链首/尾）交点越过其非相连端时，该线段的偏移段已被完全吃掉，
     *    对应位置返回 null（不渲染）；中间线段不适用。
     * 
     * @param line1 第一条线段。
     * @param line2 第二条线段。
     * @param intersectPoint 两条线段延长后的交点控制点。
     * @param line1Side line1 与 line2 相连的端（来自原始链解析）。
     * @param line2Side line2 与 line1 相连的端（来自原始链解析）。
     * @param line1IsBoundary line1 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）。
     * @param line2IsBoundary line2 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的两条新线段 [line1', line2']（被完全吃掉的边界线返回 null）。
     */
    static cutLineLine(
        line1: SketchLineObject,
        line2: SketchLineObject,
        intersectPoint: SketchPointObject,
        line1Side: 'start' | 'end', // line1 与 line2 相连的端（来自原始链解析，偏移不改变拓扑）
        line2Side: 'start' | 'end', // line2 与 line1 相连的端（来自原始链解析，偏移不改变拓扑）
        line1IsBoundary = false, // line1 是否为边界线段（开放链首/尾）
        line2IsBoundary = false, // line2 是否为边界线段（开放链首/尾）
    ): CutPairResult<SketchLineObject, SketchLineObject> {
        // 对两条线段分别截取并返回（统一走公共截取入口 cutLineByIntersection，
        // 各线独立传入自身的辅助对象属性，保持截取产物与原始链一致）
        return [
            SketchEntityCut.cutLineByIntersection(line1, intersectPoint, line1Side, line1.isAuxiliaryObject, line1IsBoundary),
            SketchEntityCut.cutLineByIntersection(line2, intersectPoint, line2Side, line2.isAuxiliaryObject, line2IsBoundary),
        ];
    }

    /**
     * 对于线段, 用交点顶替"原始链相连端"形成新的线段
     * 对于圆弧, 用交点顶替"原始链相连端"后将圆弧该端延长/截短至交点处, 形成新的圆弧
     * 返回新的 [SketchLineObject, SketchArcObject]
     * 
     * 实现说明：
     * 1. 线段：交点替换原始链相连端（保留端点角色，与 {@link cutLineLine} 单线逻辑一致）；
     * 2. 圆弧：交点替换原始链相连端（保留端点角色），弧定义中对应角度同步更新——
     *    交点顶替起点时新 startAngle = 交点相对圆心的极角（标准化到与 startAngle 同一 2π 窗口），
     *    顶替终点时新 endAngle 同理，实现"弧端延长/截短至交点处"；
     * 3. 截取侧由入参 side（原始链解析的相连端）决定，不再用"离交点最近"启发式——
     *    大偏移时交点可能越过另一端点导致选错侧；
     * 4. 交点直接复用传入实例（连接端共享）；非相连端：边界实体克隆独立实例，非边界实体
     *    复用原端点实例（必为上一轮截取的共享端点）；圆心克隆；复用前摘除旧挂载；
     * 5. 不修改原始 line1/arc，返回两条独立的新对象；
     *    边界线段（开放链首/尾）交点越过其非相连端时线段被完全吃掉，对应位置返回 null（不渲染）；
     *    中间线段不适用。
     * 
     * @param line1 线段。
     * @param arc 圆弧。
     * @param intersectPoint 线段与圆弧延长后的交点控制点。
     * @param lineSide line1 与 arc 相连的端（来自原始链解析）。
     * @param arcSide arc 与 line1 相连的端（来自原始链解析）。
     * @param lineIsBoundary line1 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）。
     * @param arcIsBoundary arc 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新线段与新圆弧 [line1', arc']（线段被完全吃掉时为 [null, arc']）。
     */
    static cutLineArc(
        line1: SketchLineObject,
        arc: SketchArcObject,
        intersectPoint: SketchPointObject,
        lineSide: 'start' | 'end', // line1 与 arc 相连的端（来自原始链解析，偏移不改变拓扑）
        arcSide: 'start' | 'end', // arc 与 line1 相连的端（来自原始链解析，偏移不改变拓扑）
        lineIsBoundary = false, // line1 是否为边界线段（开放链首/尾）
        arcIsBoundary = false, // arc 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchLineObject, SketchArcObject> {
        const isAuxiliary = line1.isAuxiliaryObject;

        // ═══════════════════════════════════════════════════════════
        // 1. 截取线段：统一走公共截取入口 cutLineByIntersection
        //    （传入线段的辅助对象属性，保持截取产物与原始链一致）
        // ═══════════════════════════════════════════════════════════
        const newLine = SketchEntityCut.cutLineByIntersection(
            line1, intersectPoint, lineSide, isAuxiliary, lineIsBoundary,
        );

        // ═══════════════════════════════════════════════════════════
        // 2. 截取圆弧：统一走公共截取入口 cutArcByIntersection
        //    （传入线段的辅助对象属性，保持截取产物与原始链一致）
        // ═══════════════════════════════════════════════════════════
        const newArc = SketchEntityCut.cutArcByIntersection(
            arc, intersectPoint, arcSide, isAuxiliary, arcIsBoundary,
        );

        // 3. 返回截取后的新线段与新圆弧
        return [newLine, newArc];
    }

    /**
     * 线的截取方式与cutLineArc函数相同, 
     * Ellipse用交点顶替"原始链相连端", 将该端延长/截短至交点, 形成新的椭圆弧
     * @param line 线段。
     * @param ellipse 椭圆弧。
     * @param intersectPoint 线段与椭圆弧延长后的交点控制点。
     * 
     * 实现说明：
     * 1. 线段：交点替换原始链相连端（保留端点角色，与 {@link cutLineArc} 线段逻辑一致）；
     * 2. 椭圆弧：交点替换原始链相连端（保留端点角色），椭圆定义中对应角度同步更新——
     *    交点变换到椭圆主轴系反解参数角 θ = atan2(y'/ry, x'/rx)（注意是椭圆参数角而非极角），
     *    交点顶替起点时新 startAngle = θ（标准化到与 startAngle 同一 2π 窗口），
     *    顶替终点时新 endAngle 同理，实现"弧端延长/截短至交点处"；
     * 3. 截取侧由入参 side（原始链解析的相连端）决定，不再用"离交点最近"启发式——
     *    大偏移时交点可能越过另一端点导致选错侧；
     * 4. 交点直接复用传入实例（连接端共享）；非相连端：边界实体克隆独立实例，非边界实体
     *    复用原端点实例（必为上一轮截取的共享端点）；中心克隆；复用前摘除旧挂载；
     * 5. 不修改原始 line/ellipse，返回两条独立的新对象；
     *    边界线段（开放链首/尾）交点越过其非相连端时线段被完全吃掉，对应位置返回 null（不渲染）；
     *    中间线段不适用。
     * 
     * @param lineIsBoundary line 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）。
     * @param ellipseIsBoundary ellipse 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新线段与新椭圆弧 [line', ellipse']（线段被完全吃掉时为 [null, ellipse']）。
     */
    static cutLineEllipse(
        line: SketchLineObject,
        ellipse: SketchEllipseObject,
        intersectPoint: SketchPointObject,
        lineSide: 'start' | 'end', // line 与 ellipse 相连的端（来自原始链解析，偏移不改变拓扑）
        ellipseSide: 'start' | 'end', // ellipse 与 line 相连的端（来自原始链解析，偏移不改变拓扑）
        lineIsBoundary = false, // line 是否为边界线段（开放链首/尾）
        ellipseIsBoundary = false, // ellipse 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchLineObject, SketchEllipseObject> {
        const isAuxiliary = line.isAuxiliaryObject;

        // ═══════════════════════════════════════════════════════════
        // 1. 截取线段：统一走公共截取入口 cutLineByIntersection
        //    （传入线段的辅助对象属性，保持截取产物与原始链一致）
        // ═══════════════════════════════════════════════════════════
        const newLine = SketchEntityCut.cutLineByIntersection(
            line, intersectPoint, lineSide, isAuxiliary, lineIsBoundary,
        );

        // ═══════════════════════════════════════════════════════════
        // 2. 截取椭圆弧：统一走公共截取入口 cutEllipseByIntersection
        //    （传入线段的辅助对象属性，保持截取产物与原始链一致）
        // ═══════════════════════════════════════════════════════════
        const newEllipse = SketchEntityCut.cutEllipseByIntersection(
            ellipse, intersectPoint, ellipseSide, isAuxiliary, ellipseIsBoundary,
        );

        // 3. 返回截取后的新线段与新椭圆弧
        return [newLine, newEllipse];
    }

    /**
     * 按交点截取线段 —— 所有 cut*Line* 的线段侧统一收口入口。
     * 
     * 设计说明：
     * 1. 先计算交点在线段方向上的投影参数 t（t < 0：交点位于起点侧之外；
     *    t > 1：交点位于终点侧之外；t ∈ [0, 1]：交点位于 A→B 之间）；
     * 2. 交点在"接缝侧的反向延长线"上（越过非相连端）时判定为偏移段被完全吃掉
     *    （如向内偏移距离超过边长），整段不渲染，直接返回 null——
     *    相连端为 end（B）时非相连端是起点 A：t < 0 即交点越过 A；
     *    相连端为 start（A）时非相连端是终点 B：t > 1 即交点越过 B。
     *    不再区分边界/中间线段：任何线段只要交点越过非相连端（落在接缝侧
     *    反向延长线上），该段在偏移轮廓中都不存在；
     *    交点越过"相连端"（另一侧）则是正常的凹角延长（保留）；
     *    容差：交点恰落在非相连端（零长度边）同样视为消失；
     * 3. 以原始链相连端（side 参数）判定截取侧，交点顶替该端点（保留端点角色）：
     *    被替换的是 startPoint 则交点成为新 startPoint，是 endPoint 则交点成为新 endPoint。
     *    偏移距离增大时交点可能越过另一端点，旧"离交点最近"启发式会选错截取侧，
     *    故截取侧必须由偏移前拓扑决定；
     * 4. 交点直接复用传入实例（连接端共享）；非相连端：边界实体（开放链首/尾）克隆独立
     *    实例，非边界实体复用原端点实例（必为上一轮截取的共享端点）——组合草图实体相连
     *    端点必须复用同一实例；复用前摘除旧挂载，避免"新实体→旧实体"引用链与点不可见；
     * 5. 不修改原始 line，返回全新独立实例（退化线段 A≈B 直接返回 null）。
     * 
     * @param line 待截取的线段。
     * @param intersectPoint 交点控制点。
     * @param side 线段与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）。
     * @param isAuxiliary 交点/端点控制点是否继承辅助对象属性（线段参与时传线段侧属性，默认 false）。
     * @param isBoundary 是否为边界线段（开放链首/尾实体，由调用方按原始链位置判定）；
     *   仅控制非相连端端点实例的克隆（true）或复用（false），不再参与消失判定。
     * @returns 截取后的新线段；交点在接缝侧反向延长线上（偏移段被完全吃掉）时返回 null。
     */
    private static cutLineByIntersection(
        line: SketchLineObject,
        intersectPoint: SketchPointObject,
        side: 'start' | 'end', // 线段与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）
        isAuxiliary = false, // 交点/端点控制点是否继承辅助对象属性（默认 false）
        isBoundary = false, // 是否为边界线段（开放链首/尾）；仅控制非相连端克隆/复用，不参与消失判定
    ): SketchLineObject | null {
        const referencePlane = line.referencePlane;

        // ═══════════════════════════════════════════════════════════
        // 1. 计算交点在线段方向上的投影参数 t，判定交点相对线段的位置
        // ═══════════════════════════════════════════════════════════
        const startWorld = line.startPoint.position;
        const endWorld = line.endPoint.position;
        const ab = endWorld.clone().sub(startWorld);
        const lenSq = ab.lengthSq();

        // 退化线段（A≈B）：偏移段无意义，无法截取，直接丢弃
        if (lenSq < 1e-24) {
            return null;
        }
        const t = intersectPoint.position.clone().sub(startWorld).dot(ab) / lenSq;

        // ═══════════════════════════════════════════════════════════
        // 2. 交点在接缝侧的反向延长线上（越过非相连端）→ 整段不渲染（返回 null）
        //    交点越过非相连端意味着该段已被相邻实体的反向延长线交点完全吃掉，
        //    在偏移轮廓中不存在（如向内偏移距离超过边长）；
        //    相连端为 end（B）时非相连端是起点 A：t < 0 即交点越过 A；
        //    相连端为 start（A）时非相连端是终点 B：t > 1 即交点越过 B。
        //    不再区分边界/中间线段，一律按几何位置判定；
        //    交点越过"相连端"（另一侧）则是凹角延长场景，必须保留。
        //    容差：交点恰落在非相连端（零长度边）同样视为消失
        // ═══════════════════════════════════════════════════════════
        if ((side === 'end' && t <= 1e-9) || (side === 'start' && t >= 1 - 1e-9)) {
            return null;
        }

        // ═══════════════════════════════════════════════════════════
        // 3. 判定截取侧：以原始链相连端（side）为准，交点顶替该端点
        // ═══════════════════════════════════════════════════════════
        const isStartSide = side === 'start';

        // 4. 判定非相连端（交点顶替相连端后保留的另一端）：
        //    边界实体（开放链首/尾）的该端永远不会被其它交点顶替共享，克隆独立实例，
        //    避免新实体绑定将被替换丢弃的偏移实体端点；
        //    非边界实体的该端必为上一轮截取的共享端点（或短路分支的共享端点），
        //    直接复用达成"组合草图实体相连端点必须复用"；
        //    复用前摘除旧挂载（该端点可能被上一轮挂载型实体 add 为 children，不摘除会
        //    形成"新实体→旧实体"引用链导致旧实体无法 GC 且点跟随废弃层级不可见）
        const nonJointPoint = isStartSide ? line.endPoint : line.startPoint;
        const newNonJointPoint = isBoundary
            ? new SketchPointObject({
                position: nonJointPoint.position.clone(),
                pointKind: SketchPointKind.ControlPoint,
                material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
                referencePlane,
                isAuxiliaryObject: isAuxiliary,
            })
            : (SketchEntityCut.detachPointFromParent(nonJointPoint), nonJointPoint);

        // 5. 交点控制点：直接复用传入实例（不再克隆），保证相连两实体的连接端共用同一实例；
        //    短路分支传入的现成端点可能仍挂载在旧实体 children 下，先摘除旧挂载
        SketchEntityCut.detachPointFromParent(intersectPoint);

        let newStartPoint: SketchPointObject;
        let newEndPoint: SketchPointObject;

        if (isStartSide) {
            // 交点顶替 startPoint（保留起点角色）；非相连端保留
            newStartPoint = intersectPoint;
            newEndPoint = newNonJointPoint;
        } else {
            // 交点顶替 endPoint（保留终点角色）；非相连端保留
            newStartPoint = newNonJointPoint;
            newEndPoint = intersectPoint;
        }

        // ═══════════════════════════════════════════════════════════
        // 6. 构造截取后的新线段（全新独立实例）
        // ═══════════════════════════════════════════════════════════
        return new SketchLineObject({
            referencePlane,
            startPoint: newStartPoint,
            endPoint: newEndPoint,
            isInfiniteLength: false,
            isAuxiliaryObject: isAuxiliary,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });
    }

    /**
     * 摘除控制点与旧父级的挂载关系 —— 端点复用时调用。
     * 
     * 背景：被复用的端点可能已被"挂载型"实体（圆弧/椭圆/抛物线/样条构造时
     * `if (!point.parent) { this.add(point) }`）挂载为 children；直接复用而不摘除会
     * 形成"新实体 → 旧实体"引用链（旧实体无法被 GC 回收），且点跟随废弃实体的层级
     * 导致不可见。摘除后由新实体构造时按需重新挂载，或由确认路径 addSketchObject 挂载。
     * 重复摘除（共享点被多个实体引用）是安全的：仅第一个引用者摘除后，后续摘除无操作。
     * 
     * @param point 待摘除挂载的控制点。
     */
    private static detachPointFromParent(point: SketchPointObject): void {
        if (point.parent) {
            point.parent.remove(point);
        }
    }

    /**
     * 按交点截取圆弧 —— 所有 cut*Arc* 的圆弧侧统一收口入口。
     * 
     * 设计说明：
     * 1. 以原始链相连端（side 参数）判定截取侧，交点顶替该端点（保留端点角色），
     *    弧定义中对应角度同步更新——交点顶替起点时新 startAngle = 交点相对圆心的极角
     *    （标准化到与 startAngle 同一 2π 窗口），顶替终点时新 endAngle 同理，
     *    实现"弧端延长/截短至交点处"；
     * 2. 截取侧由入参 side（原始链解析的相连端）决定，不再用"离交点最近"启发式——
     *    大偏移时交点可能越过另一端点导致选错侧；
     * 3. 交点直接复用传入实例（连接端共享）；非相连端：边界实体（开放链首/尾）克隆独立
     *    实例，非边界实体复用原端点实例（必为上一轮截取的共享端点）；圆心控制点仍为独立
     *    新实例（内部控制点，无共享语义）；复用前摘除旧挂载；
     * 4. 不修改原始 arc，返回全新独立实例；
     * 
     * @param arc 待截取的圆弧。
     * @param intersectPoint 交点控制点。
     * @param side 圆弧与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）。
     * @param isAuxiliary 交点/端点控制点是否继承辅助对象属性（线段参与时传线段侧属性，默认 false）。
     * @returns 截取后的新圆弧；交点"绕回"非法时返回 null（整链不渲染，由调用方升级处理）。
     */
    private static cutArcByIntersection(
        arc: SketchArcObject,
        intersectPoint: SketchPointObject,
        side: 'start' | 'end', // 圆弧与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）
        isAuxiliary = false, // 交点/端点控制点是否继承辅助对象属性（默认 false）
        isBoundary = false, // 是否为边界实体（开放链首/尾）；边界实体的非相连端克隆独立实例，非边界实体复用原端点实例
    ): SketchArcObject | null {
        const referencePlane = arc.referencePlane;
        const arcDef = arc.getArcDefinition();
        const { centerWorld, radius, startAngle, endAngle, clockwise, segments } = arcDef;
        const centerLocal = referencePlane.worldToLocal(centerWorld.clone());

        // ═══════════════════════════════════════════════════════════
        // 1. 交点相对弧圆心的极角（新弧端点角度）
        // ═══════════════════════════════════════════════════════════
        const intersectLocal = referencePlane.worldToLocal(intersectPoint.position.clone());
        const angleAtIntersection = Math.atan2(
            intersectLocal.y - centerLocal.y,
            intersectLocal.x - centerLocal.x,
        );

        // ═══════════════════════════════════════════════════════════
        // 2. 判定截取侧：以原始链相连端（side）为准，交点顶替该端点
        //    （大偏移时交点可能越过另一端点，距离判定不可靠）
        // ═══════════════════════════════════════════════════════════
        const jointAtStart = side === 'start';

        // 3. 创建控制点：
        //    交点直接复用传入实例（连接端共享）；短路分支传入的现成端点可能仍挂载在
        //    旧实体 children 下，先摘除旧挂载；
        //    非相连端：边界实体（开放链首/尾）克隆独立实例，非边界实体复用原端点实例
        //    （必为上一轮截取的共享端点），复用前同样摘除旧挂载；
        //    圆心控制点仍为独立新实例（内部控制点，无共享语义）
        SketchEntityCut.detachPointFromParent(intersectPoint);
        const arcIntersectPoint = intersectPoint;
        const cloneArcEnd = (pos: Vector3): SketchPointObject => new SketchPointObject({
            position: pos.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        });
        const newCenterPt = new SketchPointObject({
            position: centerWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: false,
        });
        // 非相连端端点实例（用于边界克隆 / 非边界复用）
        const nonJointPoint = jointAtStart ? arc.getEndPoint() : arc.getStartPoint();
        const newNonJointPoint = isBoundary
            ? cloneArcEnd(nonJointPoint.position)
            : (SketchEntityCut.detachPointFromParent(nonJointPoint), nonJointPoint);

        // ═══════════════════════════════════════════════════════════
        // 5. 构造截取后的新圆弧：交点顶替起点则更新 startAngle，顶替终点则更新 endAngle
        // ═══════════════════════════════════════════════════════════
        if (jointAtStart) {
            // 交点顶替弧起点：新 startAngle = 交点极角（标准化到与 startAngle 同一 2π 窗口）
            let newStartAngle = angleAtIntersection;
            while (newStartAngle < startAngle - Math.PI) newStartAngle += Math.PI * 2;
            while (newStartAngle >= startAngle + Math.PI) newStartAngle -= Math.PI * 2;

            return new SketchArcObject({
                referencePlane,
                definition: {
                    centerWorld: centerWorld.clone(),
                    radius,
                    startAngle: newStartAngle,
                    endAngle: arcDef.endAngle,
                    clockwise: arcDef.clockwise,
                    segments: arcDef.segments,
                },
                startPoint: arcIntersectPoint,
                endPoint: newNonJointPoint,
                centerPoint: newCenterPt,
                material: LineMaterialFactory.getLineMaterialByBaseStyle(
                    SketchEdgeBaseStyle.Basic,
                    LineMaterialState.Default,
                ),
            });
        }

        // 交点顶替弧终点：新 endAngle = 交点极角（标准化到与 endAngle 同一 2π 窗口）
        let newEndAngle = angleAtIntersection;
        while (newEndAngle < endAngle - Math.PI) newEndAngle += Math.PI * 2;
        while (newEndAngle >= endAngle + Math.PI) newEndAngle -= Math.PI * 2;

        return new SketchArcObject({
            referencePlane,
            definition: {
                centerWorld: centerWorld.clone(),
                radius,
                startAngle: arcDef.startAngle,
                endAngle: newEndAngle,
                clockwise: arcDef.clockwise,
                segments: arcDef.segments,
            },
            startPoint: newNonJointPoint,
            endPoint: arcIntersectPoint,
            centerPoint: newCenterPt,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });
    }

    /**
     * 按交点截取椭圆弧 —— 所有 cut*Ellipse* 的椭圆侧统一收口入口。
     * 
     * 设计说明：
     * 1. 以原始链相连端（side 参数）判定截取侧，交点顶替该端点（保留端点角色），
     *    椭圆定义中对应角度同步更新——交点变换到椭圆主轴系反解参数角
     *    θ = atan2(y'/ry, x'/rx)（注意是椭圆参数角而非极角），交点顶替起点时
     *    新 startAngle = θ（标准化到与 startAngle 同一 2π 窗口），顶替终点时
     *    新 endAngle 同理，实现"弧端延长/截短至交点处"；
     * 2. 截取侧由入参 side（原始链解析的相连端）决定，不再用"离交点最近"启发式——
     *    大偏移时交点可能越过另一端点导致选错侧；
     * 3. 交点直接复用传入实例（连接端共享）；非相连端：边界实体（开放链首/尾）克隆独立
     *    实例，非边界实体复用原端点实例（必为上一轮截取的共享端点）；中心控制点仍为独立
     *    新实例（内部控制点，无共享语义）；复用前摘除旧挂载；
     * 4. 不修改原始 ellipse，返回全新独立实例；
     * 
     * @param ellipse 待截取的椭圆弧。
     * @param intersectPoint 交点控制点。
     * @param side 椭圆弧与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）。
     * @param isAuxiliary 交点/端点控制点是否继承辅助对象属性（线段参与时传线段侧属性，默认 false）。
     * @returns 截取后的新椭圆弧；交点"绕回"非法时返回 null（整链不渲染，由调用方升级处理）。
     */
    private static cutEllipseByIntersection(
        ellipse: SketchEllipseObject,
        intersectPoint: SketchPointObject,
        side: 'start' | 'end', // 椭圆弧与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）
        isAuxiliary = false, // 交点/端点控制点是否继承辅助对象属性（默认 false）
        isBoundary = false, // 是否为边界实体（开放链首/尾）；边界实体的非相连端克隆独立实例，非边界实体复用原端点实例
    ): SketchEllipseObject | null {
        const referencePlane = ellipse.referencePlane;
        const ellipseDef = ellipse.getEllipseDefinition();
        const { centerWorld, radiusX, radiusY, rotation } = ellipseDef;
        const centerLocal = referencePlane.worldToLocal(centerWorld.clone());

        // 预计算旋转角正余弦，供主轴系坐标变换复用
        const cr = Math.cos(rotation);
        const sr = Math.sin(rotation);

        // ═══════════════════════════════════════════════════════════
        // 1. 交点变换到椭圆主轴系并反解参数角（新弧端点角度）
        //    参数角 θ = atan2(y'/ry, x'/rx)，其中 x'/y' 为旋转到主轴系的坐标
        // ═══════════════════════════════════════════════════════════
        const intersectLocal = referencePlane.worldToLocal(intersectPoint.position.clone());
        const qx = intersectLocal.x - centerLocal.x;
        const qy = intersectLocal.y - centerLocal.y;
        const xPrime = qx * cr + qy * sr;
        const yPrime = -qx * sr + qy * cr;
        const thetaAtIntersection = Math.atan2(yPrime / radiusY, xPrime / radiusX);

        // ═══════════════════════════════════════════════════════════
        // 2. 判定截取侧：以原始链相连端（side）为准，交点顶替该端点
        //    （大偏移时交点可能越过另一端点，距离判定不可靠）
        // ═══════════════════════════════════════════════════════════
        const jointAtStart = side === 'start';

        // 3. 创建控制点：
        //    交点直接复用传入实例（连接端共享）；短路分支传入的现成端点可能仍挂载在
        //    旧实体 children 下，先摘除旧挂载；
        //    非相连端：边界实体（开放链首/尾）克隆独立实例，非边界实体复用原端点实例
        //    （必为上一轮截取的共享端点），复用前同样摘除旧挂载；
        //    中心控制点仍为独立新实例（内部控制点，无共享语义）
        SketchEntityCut.detachPointFromParent(intersectPoint);
        const ellipseIntersectPoint = intersectPoint;
        const cloneEllipseEnd = (pos: Vector3): SketchPointObject => new SketchPointObject({
            position: pos.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        });
        const newCenterPt = new SketchPointObject({
            position: centerWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: false,
        });
        // 非相连端端点实例（用于边界克隆 / 非边界复用）
        const nonJointPoint = jointAtStart ? ellipse.getEndPoint() : ellipse.getStartPoint();
        const newNonJointPoint = isBoundary
            ? cloneEllipseEnd(nonJointPoint.position)
            : (SketchEntityCut.detachPointFromParent(nonJointPoint), nonJointPoint);

        // ═══════════════════════════════════════════════════════════
        // 5. 构造截取后的新椭圆弧：交点顶替起点则更新 startAngle，顶替终点则更新 endAngle
        // ═══════════════════════════════════════════════════════════
        if (jointAtStart) {
            // 交点顶替弧起点：新 startAngle = 交点参数角（标准化到与 startAngle 同一 2π 窗口）
            let newStartAngle = thetaAtIntersection;
            while (newStartAngle < ellipseDef.startAngle - Math.PI) newStartAngle += Math.PI * 2;
            while (newStartAngle >= ellipseDef.startAngle + Math.PI) newStartAngle -= Math.PI * 2;

            const cutEllipse = new SketchEllipseObject({
                referencePlane,
                definition: {
                    centerWorld: centerWorld.clone(),
                    radiusX,
                    radiusY,
                    rotation,
                    startAngle: newStartAngle,
                    endAngle: ellipseDef.endAngle,
                    clockwise: ellipseDef.clockwise,
                    segments: ellipseDef.segments,
                },
                startPoint: ellipseIntersectPoint,
                endPoint: newNonJointPoint,
                centerPoint: newCenterPt,
                material: LineMaterialFactory.getLineMaterialByBaseStyle(
                    SketchEdgeBaseStyle.Basic,
                    LineMaterialState.Default,
                ),
            });
            // 截取产物必须与正常绘制/等距偏移同一口径注册基准轴控制点：
            // 否则落地尺寸（curve-offset-distance）取不到 MajorPositive 标记点而静默跳过，
            // 圆心重合关系也会因圆心点未入组而无法持久化
            SketchEntityCut.registerCutEllipseCardinalControlPoints(referencePlane, cutEllipse, newCenterPt);
            return cutEllipse;
        }

        // 交点顶替弧终点：新 endAngle = 交点参数角（标准化到与 endAngle 同一 2π 窗口）
        let newEndAngle = thetaAtIntersection;
        while (newEndAngle < ellipseDef.endAngle - Math.PI) newEndAngle += Math.PI * 2;
        while (newEndAngle >= ellipseDef.endAngle + Math.PI) newEndAngle -= Math.PI * 2;

        const cutEllipse = new SketchEllipseObject({
            referencePlane,
            definition: {
                centerWorld: centerWorld.clone(),
                radiusX,
                radiusY,
                rotation,
                startAngle: ellipseDef.startAngle,
                endAngle: newEndAngle,
                clockwise: ellipseDef.clockwise,
                segments: ellipseDef.segments,
            },
            startPoint: newNonJointPoint,
            endPoint: ellipseIntersectPoint,
            centerPoint: newCenterPt,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });
        // 与起点截取分支同一口径注册基准轴控制点
        SketchEntityCut.registerCutEllipseCardinalControlPoints(referencePlane, cutEllipse, newCenterPt);
        return cutEllipse;
    }

    /**
     * 为截取后的椭圆弧注册基准轴控制点（圆心 + 四个长短轴标记点）。
     *
     * 说明：
     * 1. 与正常绘制（SketchEllipseDrawingHandler）和等距偏移本体
     *    （SketchEntityEquidistantOffset.buildOffsetEllipseWithCardinalControlPoints）
     *    使用同一工厂注册口径，保证参数求解、拖拽、持久化语义一致；
     * 2. 截取产物是全新实例，构造时不携带原椭圆的 cardinalControlPoints 注册，
     *    必须在此补齐，否则落地阶段创建 curve-offset-distance 尺寸时取不到
     *    MajorPositive 标记点（尺寸被静默跳过），等距圆心重合关系也会因
     *    圆心点未进入草图组而丢失。
     *
     * @param referencePlane 椭圆所属草图参考面。
     * @param ellipse 待注册的截取椭圆弧。
     * @param centerPoint 截取椭圆弧的圆心控制点。
     */
    private static registerCutEllipseCardinalControlPoints(
        referencePlane: ReferencePlane,
        ellipse: SketchEllipseObject,
        centerPoint: SketchPointObject,
    ): void {
        const definition = ellipse.getEllipseDefinition();
        const controlPointFactory = new SketchEllipseControlPointFactory(referencePlane);
        controlPointFactory.registerCardinalControlPoints(ellipse, {
            centerPoint,
            axisControlPoints: controlPointFactory.createAxisControlPoints({
                centerWorld: definition.centerWorld,
                radiusX: definition.radiusX,
                radiusY: definition.radiusY,
                rotation: definition.rotation,
            }),
        });
    }

    /**
     * 按交点截取样条曲线 —— 所有 cut*Spline 的样条侧统一收口入口。
     * 
     * 设计说明：
     * 1. 先在采样折线上求交点 B 的最近投影（逐段点-线段距离），据此区分两种情形：
     *    a. 交点位于端点外侧（延长线上）：交点顶替"原始链相连端"成为新端点，采样点与
     *       控制点其余部分保持不变 —— 曲线形状不变，仅沿端点切线延长（原有行为）；
     *    b. 交点位于曲线内部：删除"原始链相连端"与交点之间的采样点与控制点，交点代替
     *       该端点成为新端点 —— 修复旧实现"仅顶替端点采样点、B→A 段未删除"的缺陷
     *       （旧实现会残留多余曲线段、产生拉直弦段并使控制点与保留段脱节）；
     * 2. 被删除段对应的中间控制点按"控制点多边形弧长比例"映射到曲线弧长位置截断，
     *    保证后续控制点驱动的编辑重建只覆盖保留段；
     * 3. 顶替哪一端由入参 side（原始链相连端）决定，不再用"离交点最近"启发式——
     *    大偏移时交点可能越过另一端点导致选错侧；例外：交点位于端点外侧（延长线）
     *    时按外侧所在端顶替（几何自洽，避免交点与对侧端点组合导致曲线折叠）；
     * 4. 交点直接复用传入实例（连接端共享）；非相连端（保留段另一端）：边界实体
     *    （开放链首/尾）克隆独立实例，非边界实体复用原端点实例（必为上一轮截取的
     *    共享端点），复用前摘除旧挂载；
     * 5. 闭合样条或采样点不足时不截取，直接克隆返回；
     * 6. 不修改原始 spline，返回全新独立实例。
     * 
     * @param spline 待截取的样条曲线。
     * @param intersectPoint 交点控制点。
     * @param side 样条与相邻实体相连的端（来自原始链解析）。
     * @param isBoundary 是否为边界实体（开放链首/尾）；边界实体的非相连端克隆独立实例，非边界实体复用原端点实例。
     * @returns 截取后的新样条曲线。
     */
    private static cutSplineByIntersection(
        spline: SketchSplineObject,
        intersectPoint: SketchPointObject,
        side: 'start' | 'end', // 样条与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）
        isBoundary = false, // 是否为边界实体（开放链首/尾）
    ): SketchSplineObject {
        const referencePlane = spline.referencePlane;
        const curveMetadata = spline.getCurveMetadata();
        const curvePoints = spline.getCurvePoints();
        const fullControlPoints = spline.getControlPoints(); // [start, ...interior, end]

        // 闭合样条或采样点不足：不截取，直接克隆返回
        if (curveMetadata.closureKind === 'closed' || curvePoints.length < 2) {
            return spline.cloneSelf();
        }

        const intersectWorld = intersectPoint.position.clone();

        // 交点控制点：直接复用传入实例（连接端共享）；短路分支传入的现成端点可能仍
        // 挂载在旧实体 children 下，先摘除旧挂载（闭合样条提前返回的分支不涉及）
        SketchEntityCut.detachPointFromParent(intersectPoint);

        // ═══════════════════════════════════════════════════════════
        // 1. 求交点 B 在采样折线上的最近投影（所在段索引 + 段内参数 + 投影点）
        // ═══════════════════════════════════════════════════════════
        const segmentCount = curvePoints.length - 1;
        let nearestSegIndex = 0;
        let nearestT = 0;
        let nearestProj = curvePoints[0]!.clone();
        let nearestDistSq = Infinity;
        for (let i = 0; i < segmentCount; i++) {
            const segStart = curvePoints[i]!;
            const segDir = curvePoints[i + 1]!.clone().sub(segStart);
            const segLenSq = segDir.lengthSq();
            // 相邻采样点重合的退化段按段起点参与比较
            const t = segLenSq > 1e-24
                ? Math.max(0, Math.min(1,
                    intersectWorld.clone().sub(segStart).dot(segDir) / segLenSq))
                : 0;
            const proj = segStart.clone().addScaledVector(segDir, t);
            const distSq = intersectWorld.distanceToSquared(proj);
            if (distSq < nearestDistSq) {
                nearestDistSq = distSq;
                nearestSegIndex = i;
                nearestT = t;
                nearestProj = proj;
            }
        }

        // ═══════════════════════════════════════════════════════════
        // 2. 判定交点位置：起点外侧 / 终点外侧 / 曲线内部
        //    最近投影落在首段起点或末段终点 1e-9m 级范围内时，交点位于端点延长线上；
        //    否则交点位于曲线内部（含与内部采样点重合的情形）
        // ═══════════════════════════════════════════════════════════
        const atStartOutside = nearestProj.distanceToSquared(curvePoints[0]!) < 1e-18;
        const atEndOutside = nearestProj.distanceToSquared(curvePoints[curvePoints.length - 1]!) < 1e-18;

        // ═══════════════════════════════════════════════════════════
        // 3. 判定交点顶替哪一侧：以原始链相连端（side 参数）为准；
        //    外侧/内部仅决定"仅顶替端点"还是"删除内部段"
        // ═══════════════════════════════════════════════════════════
        const jointAtStart = side === 'start';

        // 独立控制点实例辅助：新建（交点）与克隆（原控制点）
        const createPoint = (position: Vector3): SketchPointObject => new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });
        const clonePoint = (point: SketchPointObject): SketchPointObject =>
            createPoint(point.position);

        // 新采样点列与新控制点列（完整 [start, ...interior, end]）
        let newCurvePoints: Vector3[];
        let newControlPoints: SketchPointObject[];

        if (atStartOutside || atEndOutside) {
            // ── 情形 a：交点位于端点外侧（延长线），顶替该侧端点，其余点保持不变 ──
            // 外侧情形以几何所在侧为准（外侧端与交点必然同侧，保证曲线不自折叠）；
            // 若与原始链相连端（side）矛盾，说明取到了非相连侧交点，几何自洽优先
            const outsideAtStart = atStartOutside;
            newCurvePoints = curvePoints.map((point) => point.clone());
            if (outsideAtStart) {
                newCurvePoints[0] = intersectWorld.clone();
            } else {
                newCurvePoints[newCurvePoints.length - 1] = intersectWorld.clone();
            }
            newControlPoints = fullControlPoints.map(clonePoint);
            if (outsideAtStart) {
                newControlPoints[0] = intersectPoint;
            } else {
                newControlPoints[newControlPoints.length - 1] = intersectPoint;
            }
        } else {
            // ── 情形 b：交点位于曲线内部，删除交点与较近端点之间的曲线段 ──
            // b.1 计算曲线总弧长与交点处弧长（采样折线累计），用于控制点截断映射
            const segmentLens: number[] = [];
            let totalArcLen = 0;
            for (let i = 0; i < segmentCount; i++) {
                const len = curvePoints[i]!.distanceTo(curvePoints[i + 1]!);
                segmentLens.push(len);
                totalArcLen += len;
            }
            let arcAtIntersect = 0;
            for (let i = 0; i < nearestSegIndex; i++) {
                arcAtIntersect += segmentLens[i]!;
            }
            arcAtIntersect += segmentLens[nearestSegIndex]! * nearestT;
            // 交点处弧长占曲线总弧长的比例（用于控制点截断映射）
            const arcRatio = totalArcLen > 1e-12 ? arcAtIntersect / totalArcLen : 0;

            // b.2 计算控制点多边形总弧长
            let controlTotalLen = 0;
            for (let i = 0; i < fullControlPoints.length - 1; i++) {
                controlTotalLen += fullControlPoints[i]!.position
                    .distanceTo(fullControlPoints[i + 1]!.position);
            }

            if (jointAtStart) {
                // ── 情形 b-1：side 为起点侧（相连端=起点），删除 [起点→B] 段，B 顶替起点 ──
                // 采样点：B 顶替投影所在段起点侧，保留投影段之后的所有采样点
                const keepFrom = nearestT > 0.999
                    ? Math.min(nearestSegIndex + 2, segmentCount)
                    : Math.min(nearestSegIndex + 1, segmentCount);
                newCurvePoints = [intersectWorld.clone(), ...curvePoints.slice(keepFrom)];

                // 控制点：从起点累计多边形弧长，保留第一个累计弧长 ≥ 交点比例的控制点
                // （含）至终点，B 作为新起点
                let firstKeepIndex = fullControlPoints.length - 1;
                let acc = 0;
                for (let i = 0; i < fullControlPoints.length - 1; i++) {
                    acc += fullControlPoints[i]!.position
                        .distanceTo(fullControlPoints[i + 1]!.position);
                    if (acc >= arcRatio * controlTotalLen - 1e-12) {
                        firstKeepIndex = i + 1;
                        break;
                    }
                }
                // 交点贴近起点时保留原起点本身，避免控制点数量无谓收缩
                if (arcRatio < 1e-9) {
                    firstKeepIndex = 0;
                }
                newControlPoints = [
                    intersectPoint,
                    ...fullControlPoints.slice(firstKeepIndex).map(clonePoint),
                ];
            } else {
                // ── 情形 b-2：side 为终点侧（相连端=终点），删除 [B→终点] 段，B 顶替终点 ──
                // 采样点：保留投影所在段起点（含）之前的采样点，B 作为新终点
                const keepTo = nearestT < 0.001
                    ? Math.max(nearestSegIndex, 1)
                    : nearestSegIndex + 1;
                newCurvePoints = [
                    ...curvePoints.slice(0, keepTo),
                    intersectWorld.clone(),
                ];

                // 控制点：从起点累计多边形弧长，保留累计弧长 ≤ 交点比例的控制点
                // （含），B 作为新终点
                let lastKeepIndex = 0;
                let acc = 0;
                for (let i = 0; i < fullControlPoints.length - 1; i++) {
                    acc += fullControlPoints[i]!.position
                        .distanceTo(fullControlPoints[i + 1]!.position);
                    if (acc <= arcRatio * controlTotalLen + 1e-12) {
                        lastKeepIndex = i + 1;
                    } else {
                        break;
                    }
                }
                // 交点贴近终点时保留原终点本身
                if (arcRatio > 1 - 1e-9) {
                    lastKeepIndex = fullControlPoints.length - 1;
                }
                newControlPoints = [
                    ...fullControlPoints.slice(0, lastKeepIndex + 1).map(clonePoint),
                    intersectPoint,
                ];
            }
        }

        // 非相连端复用：边界实体（开放链首/尾）保持上方克隆的独立实例；
        // 非边界实体复用原端点实例（必为上一轮截取的共享端点），复用前摘除旧挂载。
        // 非相连端位置：情形 a 按外侧顶替侧取反，情形 b 按相连端（side）取反
        if (!isBoundary) {
            const atOutside = atStartOutside || atEndOutside;
            const nonJointAtStart = atOutside ? atStartOutside : jointAtStart;
            const nonJointIndex = nonJointAtStart ? newControlPoints.length - 1 : 0;
            const nonJointPoint = nonJointAtStart
                ? fullControlPoints[fullControlPoints.length - 1]!
                : fullControlPoints[0]!;
            SketchEntityCut.detachPointFromParent(nonJointPoint);
            newControlPoints[nonJointIndex] = nonJointPoint;
        }

        // ═══════════════════════════════════════════════════════════
        // 4. 构造截取后的新样条曲线（全新独立实例）
        // ═══════════════════════════════════════════════════════════
        return new SketchSplineObject({
            referencePlane,
            startPoint: newControlPoints[0]!,
            endPoint: newControlPoints[newControlPoints.length - 1]!,
            controlPoints: newControlPoints.slice(1, -1),
            curvePoints: newCurvePoints,
            curveMetadata,
            material: LineMaterialFactory.getLineMaterialByBaseStyle(
                SketchEdgeBaseStyle.Basic,
                LineMaterialState.Default,
            ),
        });
    }

    /**
     * 按交点截取抛物线 —— 所有 cut*Parabola 的抛物线侧统一收口入口。
     * 
     * 设计说明：
     * 1. 先在参考面内推导抛物线局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）：
     *    顶点为原点、轴向 axisDir = 焦点-顶点向量投影到参考面、侧向 sideDir = axisDir × 法线、
     *    焦距 focalLength = 投影后的轴长；该框架下抛物线为标准形 P(t) = apex + side·t + axis·t²/(4f)；
     * 2. 交点相对顶点的侧向参数 t = (P - apex)·side 即交点处的新端点参数（交点已在抛物线上，投影即参数）；
     * 3. 以原始链相连端（side 参数）判定截取侧，交点顶替该端点（保留端点角色），
     *    对应更新 startParameter / endParameter —— 大偏移时交点可能越过另一端点，
     *    "离交点最近"启发式不可靠，故截取侧必须由偏移前拓扑决定；
     * 4. 焦点/顶点仍为独立新实例（内部控制点，无共享语义）；交点直接复用传入实例（连接端
     *    共享）；非相连端：边界实体（开放链首/尾）克隆独立实例，非边界实体复用原端点实例
     *    （必为上一轮截取的共享端点），复用前摘除旧挂载；
     * 5. 焦点与顶点重合（焦距退化）时无法构建框架，直接克隆返回；
     * 6. 不修改原始 parabola，返回全新独立实例。
     * 
     * @param parabola 待截取的抛物线。
     * @param intersectPoint 交点控制点。
     * @param side 抛物线与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）。
     * @param isAuxiliary 交点/端点控制点是否继承辅助对象属性（线段参与时传线段侧属性，默认 false）。
     * @param isBoundary 是否为边界实体（开放链首/尾）；边界实体的非相连端克隆独立实例，非边界实体复用原端点实例。
     * @returns 截取后的新抛物线。
     */
    private static cutParabolaByIntersection(
        parabola: SketchParabolaObject,
        intersectPoint: SketchPointObject,
        side: 'start' | 'end', // 抛物线与相邻实体相连的端（来自原始链解析，偏移不改变拓扑）
        isAuxiliary = false, // 交点/端点控制点是否继承辅助对象属性（线段参与时传线段侧属性）
        isBoundary = false, // 是否为边界实体（开放链首/尾）
    ): SketchParabolaObject {
        const referencePlane = parabola.referencePlane;
        const definition = parabola.getParabolaDefinition();
        const { focalWorld, apexWorld, startParameter, endParameter, segments } = definition;

        // ═══════════════════════════════════════════════════════════
        // 1. 推导抛物线局部框架（复刻 SketchParabolaObject.resolveFrameFromDefinition）
        //    顶点为原点、轴向 = 焦点-顶点向量投影到参考面、侧向 = 轴向 × 法线、焦距 = 投影轴长
        // ═══════════════════════════════════════════════════════════
        const normal = referencePlane.normal.clone().normalize();
        const axisVector = focalWorld.clone().sub(apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const focalLength = axisVector.length();

        // 定义无效防御：焦点与顶点重合时无法构建框架，直接克隆返回
        if (focalLength < 1e-12) {
            return parabola.cloneSelf();
        }

        const axisDirWorld = axisVector.normalize();
        const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();
        const apexLocal = referencePlane.worldToLocal(apexWorld);
        const side2D = referencePlane.worldToLocal(apexWorld.clone().add(sideDirWorld)).sub(apexLocal);

        // ═══════════════════════════════════════════════════════════
        // 2. 交点相对抛物线顶点的侧向参数 t = (P - apex)·side（交点已在抛物线上，投影即参数）
        // ═══════════════════════════════════════════════════════════
        const intersectLocal = referencePlane.worldToLocal(intersectPoint.position.clone());
        const tAtIntersection = (intersectLocal.x - apexLocal.x) * side2D.x
            + (intersectLocal.y - apexLocal.y) * side2D.y;

        // ═══════════════════════════════════════════════════════════
        // 3. 判定截取侧：以原始链相连端（side 参数）为准，交点顶替该端点
        //    （大偏移时交点可能越过另一端点，"离交点最近"启发式不可靠）
        // ═══════════════════════════════════════════════════════════
        const jointAtStart = side === 'start';

        // 控制点：焦点/顶点仍为独立新实例（内部控制点，无共享语义）；
        // 交点直接复用传入实例（连接端共享），短路分支传入的现成端点先摘除旧挂载；
        // 非相连端：边界实体（开放链首/尾）克隆独立实例，非边界实体复用原端点实例
        const newFocalPt = new SketchPointObject({
            position: focalWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });
        const newApexPt = new SketchPointObject({
            position: apexWorld.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane,
        });
        SketchEntityCut.detachPointFromParent(intersectPoint);
        const parabolaIntersectPoint = intersectPoint;
        const cloneParabolaEnd = (pos: Vector3): SketchPointObject => new SketchPointObject({
            position: pos.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane,
            isAuxiliaryObject: isAuxiliary,
        });
        // 非相连端端点实例（用于边界克隆 / 非边界复用）
        const nonJointPoint = jointAtStart ? parabola.getEndPoint() : parabola.getStartPoint();
        const newNonJointPoint = isBoundary
            ? cloneParabolaEnd(nonJointPoint.position)
            : (SketchEntityCut.detachPointFromParent(nonJointPoint), nonJointPoint);

        // ═══════════════════════════════════════════════════════════
        // 4. 构造截取后的新抛物线：交点顶替起点则更新 startParameter，顶替终点则更新 endParameter
        // ═══════════════════════════════════════════════════════════
        return jointAtStart
            ? new SketchParabolaObject({
                referencePlane,
                definition: {
                    focalWorld: focalWorld.clone(),
                    apexWorld: apexWorld.clone(),
                    startParameter: tAtIntersection,
                    endParameter: definition.endParameter,
                    segments,
                },
                focalPoint: newFocalPt,
                apexPoint: newApexPt,
                startPoint: parabolaIntersectPoint,
                endPoint: newNonJointPoint,
                material: LineMaterialFactory.getLineMaterialByBaseStyle(
                    SketchEdgeBaseStyle.Basic,
                    LineMaterialState.Default,
                ),
            })
            : new SketchParabolaObject({
                referencePlane,
                definition: {
                    focalWorld: focalWorld.clone(),
                    apexWorld: apexWorld.clone(),
                    startParameter: definition.startParameter,
                    endParameter: tAtIntersection,
                    segments,
                },
                focalPoint: newFocalPt,
                apexPoint: newApexPt,
                startPoint: newNonJointPoint,
                endPoint: parabolaIntersectPoint,
                material: LineMaterialFactory.getLineMaterialByBaseStyle(
                    SketchEdgeBaseStyle.Basic,
                    LineMaterialState.Default,
                ),
            });
    }

    /**
     * 线段的截取方式同上
     * 样条曲线统一走 {@link cutSplineByIntersection} 截取：交点位于曲线内部时删除
     * 原始链相连端与交点之间的曲线段、交点顶替相连端成为新端点；交点位于端点延长线上时
     * 按外侧所在端顶替（曲线沿切线延长，形状不变）。
     * @param line 线段。
     * @param spline 样条曲线。
     * @param intersectPoint 线段与样条曲线延长后的交点控制点。
     * 
     * 实现说明：
     * 1. 线段：交点替换原始链相连端（保留端点角色，与 {@link cutLineArc} 线段逻辑一致）；
     * 2. 样条曲线：委托 {@link cutSplineByIntersection} 完成截取（顶替侧由 splineSide 指定）；
     * 3. 截取侧由入参 side（原始链解析的相连端）决定，不再用"离交点最近"启发式；
     * 4. 交点直接复用传入实例（连接端共享）；非相连端：边界实体克隆独立实例，非边界实体
     *    复用原端点实例（必为上一轮截取的共享端点）；复用前摘除旧挂载；
     *    不修改原始 line/spline；
     *    边界线段（开放链首/尾）交点越过其非相连端时线段被完全吃掉，对应位置返回 null（不渲染）；
     *    中间线段不适用。
     * 
     * @param lineIsBoundary line 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）。
     * @param splineIsBoundary spline 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新线段与新样条曲线 [line', spline']（线段被完全吃掉时为 [null, spline']）。
     */
    static cutLineSpline(
        line: SketchLineObject,
        spline: SketchSplineObject,
        intersectPoint: SketchPointObject,
        lineSide: 'start' | 'end', // line 与 spline 相连的端（来自原始链解析，偏移不改变拓扑）
        splineSide: 'start' | 'end', // spline 与 line 相连的端（来自原始链解析，偏移不改变拓扑）
        lineIsBoundary = false, // line 是否为边界线段（开放链首/尾）
        splineIsBoundary = false, // spline 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchLineObject, SketchSplineObject> {
        const isAuxiliary = line.isAuxiliaryObject;

        // ═══════════════════════════════════════════════════════════
        // 1. 截取线段：统一走公共截取入口 cutLineByIntersection
        //    （传入线段的辅助对象属性，保持截取产物与原始链一致）
        // ═══════════════════════════════════════════════════════════
        const newLine = SketchEntityCut.cutLineByIntersection(
            line, intersectPoint, lineSide, isAuxiliary, lineIsBoundary,
        );

        // ═══════════════════════════════════════════════════════════
        // 2. 截取样条曲线：统一走公共截取入口 cutSplineByIntersection
        //    （交点位于曲线内部时删除交点与较近端点之间的曲线段，交点顶替端点成为新端点；
        //     交点位于端点延长线上时仅顶替端点，保持原有行为）
        // ═══════════════════════════════════════════════════════════
        const newSpline = SketchEntityCut.cutSplineByIntersection(
            spline, intersectPoint, splineSide, splineIsBoundary,
        );

        // 3. 返回截取后的新线段与新样条曲线
        return [newLine, newSpline];
    }

    /**
     * 线段的截取方式同上
     * 抛物线的截取方式：用交点顶替"原始链相连端"成为新的端点，返回新的抛物线
     * 
     * 实现说明：
     * 1. 线段：交点替换原始链相连端（保留端点角色，与 {@link cutLineArc} 线段逻辑一致）；
     * 2. 抛物线：交点替换原始链相连端（保留端点角色），参数区间同步更新；
     * 3. 截取侧由入参 side（原始链解析的相连端）决定，不再用"离交点最近"启发式——
     *    大偏移时交点可能越过另一端点导致选错侧；
     *    边界线段（开放链首/尾）交点越过其非相连端时线段被完全吃掉，对应位置返回 null（不渲染）；
     *    中间线段不适用。
     * 
     * @param line 线段。
     * @param parabola 抛物线。
     * @param intersectPoint 线段与抛物线延长后的交点控制点。
     * @param lineSide line 与 parabola 相连的端（来自原始链解析）。
     * @param parabolaSide parabola 与 line 相连的端（来自原始链解析）。
     * @param lineIsBoundary line 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）。
     * @param parabolaIsBoundary parabola 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新线段与新抛物线 [line', parabola']（线段被完全吃掉时为 [null, parabola']）。
     */
    static cutLineParabola(
        line: SketchLineObject,
        parabola: SketchParabolaObject,
        intersectPoint: SketchPointObject,
        lineSide: 'start' | 'end', // line 与 parabola 相连的端（来自原始链解析，偏移不改变拓扑）
        parabolaSide: 'start' | 'end', // parabola 与 line 相连的端（来自原始链解析，偏移不改变拓扑）
        lineIsBoundary = false, // line 是否为边界线段（开放链首/尾）
        parabolaIsBoundary = false, // parabola 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchLineObject, SketchParabolaObject> {
        const isAuxiliary = line.isAuxiliaryObject;

        // ═══ 1. 截取线段：统一走公共截取入口 cutLineByIntersection
        //     （传入线段的辅助对象属性，保持截取产物与原始链一致） ═══
        const newLine = SketchEntityCut.cutLineByIntersection(
            line, intersectPoint, lineSide, isAuxiliary, lineIsBoundary,
        );

        // ═══ 2. 截取抛物线：统一走公共截取入口 cutParabolaByIntersection ═══
        //     传入线段的辅助对象属性，保持截取产物与原始链一致
        const newParabola = SketchEntityCut.cutParabolaByIntersection(
            parabola, intersectPoint, parabolaSide, isAuxiliary, parabolaIsBoundary,
        );

        // 3. 返回截取后的新线段与新抛物线
        return [newLine, newParabola];
    }

    /**
     * 两段圆弧的截取方式：分别用交点顶替各圆弧的"原始链相连端"，返回新的两段圆弧
     * 
     * @param arc1 第一段圆弧。
     * @param arc2 第二段圆弧。
     * @param intersectPoint 两段圆弧延长后的交点控制点。
     * @param arc1Side arc1 与 arc2 相连的端（来自原始链解析）。
     * @param arc2Side arc2 与 arc1 相连的端（来自原始链解析）。
     * @param arc1IsBoundary arc1 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param arc2IsBoundary arc2 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的两段新圆弧 [arc1', arc2']（对应弧交点"绕回"非法时为 null，由调用方升级为整链不渲染）。
     */
    static cutArcArc(
        arc1: SketchArcObject,
        arc2: SketchArcObject,
        intersectPoint: SketchPointObject,
        arc1Side: 'start' | 'end', // arc1 与 arc2 相连的端（来自原始链解析，偏移不改变拓扑）
        arc2Side: 'start' | 'end', // arc2 与 arc1 相连的端（来自原始链解析，偏移不改变拓扑）
        arc1IsBoundary = false, // arc1 是否为边界实体（开放链首/尾）
        arc2IsBoundary = false, // arc2 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchArcObject, SketchArcObject> {
        // 对两段圆弧分别截取并返回（统一走公共截取入口 cutArcByIntersection，顶替侧由各自相连端指定；
        // 圆弧本身不携带辅助对象状态，isAuxiliary 保持默认 false，仅透传各自的边界标志）
        return [
            SketchEntityCut.cutArcByIntersection(arc1, intersectPoint, arc1Side, false, arc1IsBoundary),
            SketchEntityCut.cutArcByIntersection(arc2, intersectPoint, arc2Side, false, arc2IsBoundary),
        ];
    }

    /**
     * 圆弧与椭圆弧的截取方式：分别用交点顶替各实体的"原始链相连端"，返回新的圆弧与椭圆弧
     * 
     * @param arc 圆弧。
     * @param ellipse 椭圆弧。
     * @param intersectPoint 圆弧与椭圆弧延长后的交点控制点。
     * @param arcSide arc 与 ellipse 相连的端（来自原始链解析）。
     * @param ellipseSide ellipse 与 arc 相连的端（来自原始链解析）。
     * @param arcIsBoundary arc 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param ellipseIsBoundary ellipse 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新圆弧与新椭圆弧 [arc', ellipse']（对应弧/椭圆弧交点"绕回"非法时为 null，由调用方升级为整链不渲染）。
     */
    static cutArcEllipse(
        arc: SketchArcObject,
        ellipse: SketchEllipseObject,
        intersectPoint: SketchPointObject,
        arcSide: 'start' | 'end', // arc 与 ellipse 相连的端（来自原始链解析，偏移不改变拓扑）
        ellipseSide: 'start' | 'end', // ellipse 与 arc 相连的端（来自原始链解析，偏移不改变拓扑）
        arcIsBoundary = false, // arc 是否为边界实体（开放链首/尾）
        ellipseIsBoundary = false, // ellipse 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchArcObject, SketchEllipseObject> {
        // ═══ 1. 截取圆弧：统一走公共截取入口 cutArcByIntersection ═══
        const newArc = SketchEntityCut.cutArcByIntersection(
            arc, intersectPoint, arcSide, false, arcIsBoundary,
        );

        // ═══ 2. 截取椭圆弧：统一走公共截取入口 cutEllipseByIntersection ═══
        const newEllipse = SketchEntityCut.cutEllipseByIntersection(
            ellipse, intersectPoint, ellipseSide, false, ellipseIsBoundary,
        );

        // 3. 返回截取后的新圆弧与新椭圆弧
        return [newArc, newEllipse];
    }

    /**
     * 圆弧与抛物线的截取方式：分别用交点顶替各实体的"原始链相连端"，返回新的圆弧与抛物线
     * 
     * @param arc 圆弧。
     * @param parabola 抛物线。
     * @param intersectPoint 圆弧与抛物线延长后的交点控制点。
     * @param arcSide arc 与 parabola 相连的端（来自原始链解析）。
     * @param parabolaSide parabola 与 arc 相连的端（来自原始链解析）。
     * @param arcIsBoundary arc 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param parabolaIsBoundary parabola 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新圆弧与新抛物线 [arc', parabola']（弧交点"绕回"非法时对应位置为 null，由调用方升级为整链不渲染）。
     */
    static cutArcParabola(
        arc: SketchArcObject,
        parabola: SketchParabolaObject,
        intersectPoint: SketchPointObject,
        arcSide: 'start' | 'end', // arc 与 parabola 相连的端（来自原始链解析，偏移不改变拓扑）
        parabolaSide: 'start' | 'end', // parabola 与 arc 相连的端（来自原始链解析，偏移不改变拓扑）
        arcIsBoundary = false, // arc 是否为边界实体（开放链首/尾）
        parabolaIsBoundary = false, // parabola 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchArcObject, SketchParabolaObject> {
        // ═══ 1. 截取圆弧：统一走公共截取入口 cutArcByIntersection ═══
        const newArc = SketchEntityCut.cutArcByIntersection(
            arc, intersectPoint, arcSide, false, arcIsBoundary,
        );

        // ═══ 2. 截取抛物线：统一走公共截取入口 cutParabolaByIntersection ═══
        const newParabola = SketchEntityCut.cutParabolaByIntersection(
            parabola, intersectPoint, parabolaSide, false, parabolaIsBoundary,
        );

        // 3. 返回截取后的新圆弧与新抛物线
        return [newArc, newParabola];
    }

    /**
     * 圆弧与样条曲线的截取方式：圆弧用交点顶替"原始链相连端"；样条统一走
     * {@link cutSplineByIntersection} 截取（交点位于曲线内部时删除相连端与交点
     * 之间的曲线段）。返回新的圆弧与样条曲线
     * 
     * @param arc 圆弧。
     * @param spline 样条曲线。
     * @param intersectPoint 圆弧与样条曲线延长后的交点控制点。
     * @param arcSide arc 与 spline 相连的端（来自原始链解析）。
     * @param splineSide spline 与 arc 相连的端（来自原始链解析）。
     * @param arcIsBoundary arc 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param splineIsBoundary spline 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新圆弧与新样条曲线 [arc', spline']（弧交点"绕回"非法时对应位置为 null，由调用方升级为整链不渲染）。
     */
    static cutArcSpline(
        arc: SketchArcObject,
        spline: SketchSplineObject,
        intersectPoint: SketchPointObject,
        arcSide: 'start' | 'end', // arc 与 spline 相连的端（来自原始链解析，偏移不改变拓扑）
        splineSide: 'start' | 'end', // spline 与 arc 相连的端（来自原始链解析，偏移不改变拓扑）
        arcIsBoundary = false, // arc 是否为边界实体（开放链首/尾）
        splineIsBoundary = false, // spline 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchArcObject, SketchSplineObject> {
        // ═══ 1. 截取圆弧：统一走公共截取入口 cutArcByIntersection ═══
        const newArc = SketchEntityCut.cutArcByIntersection(
            arc, intersectPoint, arcSide, false, arcIsBoundary,
        );

        // ═══ 2. 截取样条曲线：统一走公共截取入口 cutSplineByIntersection ═══
        const newSpline = SketchEntityCut.cutSplineByIntersection(
            spline, intersectPoint, splineSide, splineIsBoundary,
        );

        // 3. 返回截取后的新圆弧与新样条曲线
        return [newArc, newSpline];
    }

    /**
     * 两段椭圆弧的截取方式：分别用交点顶替各椭圆弧的"原始链相连端"，返回新的两段椭圆弧
     * 
     * @param ellipse1 第一段椭圆弧。
     * @param ellipse2 第二段椭圆弧。
     * @param intersectPoint 两段椭圆弧延长后的交点控制点。
     * @param ellipse1Side ellipse1 与 ellipse2 相连的端（来自原始链解析）。
     * @param ellipse2Side ellipse2 与 ellipse1 相连的端（来自原始链解析）。
     * @param ellipse1IsBoundary ellipse1 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param ellipse2IsBoundary ellipse2 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的两段新椭圆弧 [ellipse1', ellipse2']（对应椭圆弧交点"绕回"非法时为 null，由调用方升级为整链不渲染）。
     */
    static cutEllipseEllipse(
        ellipse1: SketchEllipseObject,
        ellipse2: SketchEllipseObject,
        intersectPoint: SketchPointObject,
        ellipse1Side: 'start' | 'end', // ellipse1 与 ellipse2 相连的端（来自原始链解析，偏移不改变拓扑）
        ellipse2Side: 'start' | 'end', // ellipse2 与 ellipse1 相连的端（来自原始链解析，偏移不改变拓扑）
        ellipse1IsBoundary = false, // ellipse1 是否为边界实体（开放链首/尾）
        ellipse2IsBoundary = false, // ellipse2 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchEllipseObject, SketchEllipseObject> {
        // 对两段椭圆弧分别截取并返回（统一走公共截取入口 cutEllipseByIntersection，顶替侧由各自相连端指定；
        // 椭圆弧本身不携带辅助对象状态，isAuxiliary 保持默认 false，仅透传各自的边界标志）
        return [
            SketchEntityCut.cutEllipseByIntersection(ellipse1, intersectPoint, ellipse1Side, false, ellipse1IsBoundary),
            SketchEntityCut.cutEllipseByIntersection(ellipse2, intersectPoint, ellipse2Side, false, ellipse2IsBoundary),
        ];
    }

    /**
     * 椭圆弧与样条曲线的截取方式：椭圆弧用交点顶替"原始链相连端"；样条统一走
     * {@link cutSplineByIntersection} 截取（交点位于曲线内部时删除相连端与交点
     * 之间的曲线段）。返回新的椭圆弧与样条曲线
     * 
     * @param ellipse 椭圆弧。
     * @param spline 样条曲线。
     * @param intersectPoint 椭圆弧与样条曲线延长后的交点控制点。
     * @param ellipseSide ellipse 与 spline 相连的端（来自原始链解析）。
     * @param splineSide spline 与 ellipse 相连的端（来自原始链解析）。
     * @param ellipseIsBoundary ellipse 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param splineIsBoundary spline 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新椭圆弧与新样条曲线 [ellipse', spline']（椭圆弧交点"绕回"非法时对应位置为 null，由调用方升级为整链不渲染）。
     */
    static cutEllipseSpline(
        ellipse: SketchEllipseObject,
        spline: SketchSplineObject,
        intersectPoint: SketchPointObject,
        ellipseSide: 'start' | 'end', // ellipse 与 spline 相连的端（来自原始链解析，偏移不改变拓扑）
        splineSide: 'start' | 'end', // spline 与 ellipse 相连的端（来自原始链解析，偏移不改变拓扑）
        ellipseIsBoundary = false, // ellipse 是否为边界实体（开放链首/尾）
        splineIsBoundary = false, // spline 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchEllipseObject, SketchSplineObject> {
        // ═══ 1. 截取椭圆弧：统一走公共截取入口 cutEllipseByIntersection ═══
        const newEllipse = SketchEntityCut.cutEllipseByIntersection(
            ellipse, intersectPoint, ellipseSide, false, ellipseIsBoundary,
        );

        // ═══ 2. 截取样条曲线：统一走公共截取入口 cutSplineByIntersection ═══
        const newSpline = SketchEntityCut.cutSplineByIntersection(
            spline, intersectPoint, splineSide, splineIsBoundary,
        );

        // 3. 返回截取后的新椭圆弧与新样条曲线
        return [newEllipse, newSpline];
    }

    /**
     * 椭圆弧与抛物线的截取方式：分别用交点顶替各实体的"原始链相连端"，返回新的椭圆弧与抛物线
     * 
     * @param ellipse 椭圆弧。
     * @param parabola 抛物线。
     * @param intersectPoint 椭圆弧与抛物线延长后的交点控制点。
     * @param ellipseSide ellipse 与 parabola 相连的端（来自原始链解析）。
     * @param parabolaSide parabola 与 ellipse 相连的端（来自原始链解析）。
     * @param ellipseIsBoundary ellipse 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param parabolaIsBoundary parabola 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新椭圆弧与新抛物线 [ellipse', parabola']（椭圆弧交点"绕回"非法时对应位置为 null，由调用方升级为整链不渲染）。
     */
    static cutEllipseParabola(
        ellipse: SketchEllipseObject,
        parabola: SketchParabolaObject,
        intersectPoint: SketchPointObject,
        ellipseSide: 'start' | 'end', // ellipse 与 parabola 相连的端（来自原始链解析，偏移不改变拓扑）
        parabolaSide: 'start' | 'end', // parabola 与 ellipse 相连的端（来自原始链解析，偏移不改变拓扑）
        ellipseIsBoundary = false, // ellipse 是否为边界实体（开放链首/尾）
        parabolaIsBoundary = false, // parabola 是否为边界实体（开放链首/尾）
    ): CutPairResult<SketchEllipseObject, SketchParabolaObject> {
        // ═══ 1. 截取椭圆弧：统一走公共截取入口 cutEllipseByIntersection ═══
        const newEllipse = SketchEntityCut.cutEllipseByIntersection(
            ellipse, intersectPoint, ellipseSide, false, ellipseIsBoundary,
        );

        // ═══ 2. 截取抛物线：统一走公共截取入口 cutParabolaByIntersection ═══
        const newParabola = SketchEntityCut.cutParabolaByIntersection(
            parabola, intersectPoint, parabolaSide, false, parabolaIsBoundary,
        );

        // 3. 返回截取后的新椭圆弧与新抛物线
        return [newEllipse, newParabola];
    }

    /**
     * 两段样条曲线的截取方式：分别走 {@link cutSplineByIntersection} 截取
     * （顶替侧由各自相连端指定），返回新的两段样条曲线
     * 
     * @param spline1 第一段样条。
     * @param spline2 第二段样条。
     * @param intersectPoint 两段样条曲线延长后的交点控制点。
     * @param spline1Side spline1 与 spline2 相连的端（来自原始链解析）。
     * @param spline2Side spline2 与 spline1 相连的端（来自原始链解析）。
     * @param spline1IsBoundary spline1 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param spline2IsBoundary spline2 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的两段新样条曲线 [spline1', spline2']。
     */
    static cutSplineSpline(
        spline1: SketchSplineObject,
        spline2: SketchSplineObject,
        intersectPoint: SketchPointObject,
        spline1Side: 'start' | 'end', // spline1 与 spline2 相连的端（来自原始链解析，偏移不改变拓扑）
        spline2Side: 'start' | 'end', // spline2 与 spline1 相连的端（来自原始链解析，偏移不改变拓扑）
        spline1IsBoundary = false, // spline1 是否为边界实体（开放链首/尾）
        spline2IsBoundary = false, // spline2 是否为边界实体（开放链首/尾）
    ): [SketchSplineObject, SketchSplineObject] {
        // 截取单条样条：统一走公共截取入口 cutSplineByIntersection（顶替侧由 side 指定，边界标志透传）
        const cutSingleSpline = (spline: SketchSplineObject, side: 'start' | 'end', isBoundary: boolean): SketchSplineObject =>
            SketchEntityCut.cutSplineByIntersection(spline, intersectPoint, side, isBoundary);

        // 对两条样条分别截取并返回
        return [
            cutSingleSpline(spline1, spline1Side, spline1IsBoundary),
            cutSingleSpline(spline2, spline2Side, spline2IsBoundary),
        ];
    }

    /**
     * 样条曲线与抛物线的截取方式：样条统一走 {@link cutSplineByIntersection} 截取
     * （交点位于曲线内部时删除相连端与交点之间的曲线段）；抛物线用交点顶替
     * "原始链相连端"。返回新的样条曲线与抛物线
     * 
     * @param spline 样条曲线。
     * @param parabola 抛物线。
     * @param intersectPoint 样条曲线与抛物线延长后的交点控制点。
     * @param splineSide spline 与 parabola 相连的端（来自原始链解析）。
     * @param parabolaSide parabola 与 spline 相连的端（来自原始链解析）。
     * @param splineIsBoundary spline 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param parabolaIsBoundary parabola 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的新样条曲线与新抛物线 [spline', parabola']。
     */
    static cutSplineParabola(
        spline: SketchSplineObject,
        parabola: SketchParabolaObject,
        intersectPoint: SketchPointObject,
        splineSide: 'start' | 'end', // spline 与 parabola 相连的端（来自原始链解析，偏移不改变拓扑）
        parabolaSide: 'start' | 'end', // parabola 与 spline 相连的端（来自原始链解析，偏移不改变拓扑）
        splineIsBoundary = false, // spline 是否为边界实体（开放链首/尾）
        parabolaIsBoundary = false, // parabola 是否为边界实体（开放链首/尾）
    ): [SketchSplineObject, SketchParabolaObject] {
        // ═══ 1. 截取样条曲线：统一走公共截取入口 cutSplineByIntersection ═══
        const curveMetadata = spline.getCurveMetadata();
        const curvePoints = spline.getCurvePoints();

        // 闭合样条或采样点不足：样条不截取，直接克隆返回
        if (curveMetadata.closureKind === 'closed' || curvePoints.length < 2) {
            return [spline.cloneSelf(), parabola.cloneSelf()];
        }
        const newSpline = SketchEntityCut.cutSplineByIntersection(
            spline, intersectPoint, splineSide, splineIsBoundary,
        );

        // ═══ 2. 截取抛物线：统一走公共截取入口 cutParabolaByIntersection ═══
        const newParabola = SketchEntityCut.cutParabolaByIntersection(
            parabola, intersectPoint, parabolaSide, false, parabolaIsBoundary,
        );

        // 3. 返回截取后的新样条曲线与新抛物线
        return [newSpline, newParabola];
    }

    /**
     * 两条抛物线的截取方式：分别用交点顶替各抛物线的"原始链相连端"，返回新的两条抛物线
     * 
     * @param parabola1 第一段抛物线。
     * @param parabola2 第二段抛物线。
     * @param intersectPoint 两条抛物线延长后的交点控制点。
     * @param parabola1Side parabola1 与 parabola2 相连的端（来自原始链解析）。
     * @param parabola2Side parabola2 与 parabola1 相连的端（来自原始链解析）。
     * @param parabola1IsBoundary parabola1 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @param parabola2IsBoundary parabola2 是否为边界实体（开放链首/尾，由调用方按原始链位置判定）。
     * @returns 截取后的两条新抛物线 [parabola1', parabola2']。
     */
    static cutParabolaParabola(
        parabola1: SketchParabolaObject,
        parabola2: SketchParabolaObject,
        intersectPoint: SketchPointObject,
        parabola1Side: 'start' | 'end', // parabola1 与 parabola2 相连的端（来自原始链解析，偏移不改变拓扑）
        parabola2Side: 'start' | 'end', // parabola2 与 parabola1 相连的端（来自原始链解析，偏移不改变拓扑）
        parabola1IsBoundary = false, // parabola1 是否为边界实体（开放链首/尾）
        parabola2IsBoundary = false, // parabola2 是否为边界实体（开放链首/尾）
    ): [SketchParabolaObject, SketchParabolaObject] {
        // 对两条抛物线分别截取并返回（统一走公共截取入口 cutParabolaByIntersection，顶替侧由各自相连端指定；
        // 抛物线本身不携带辅助对象状态，isAuxiliary 保持默认 false，仅透传各自的边界标志）
        return [
            SketchEntityCut.cutParabolaByIntersection(parabola1, intersectPoint, parabola1Side, false, parabola1IsBoundary),
            SketchEntityCut.cutParabolaByIntersection(parabola2, intersectPoint, parabola2Side, false, parabola2IsBoundary),
        ];
    }

}
